import type React from 'react';

import { Translate } from '@change-corgi/core/react/i18n';
import { Button } from '@change-corgi/design-system-legacy/components/actions';
import { Loader } from '@change-corgi/design-system-legacy/components/progressiveDisclosure';
import { Flex } from '@change-corgi/design-system-legacy/layout';
import { Text } from '@change-corgi/design-system-legacy/typography';

import type { StarterDashboardPageData } from 'src/app/pages/starterDashboard/pageContext';
import { ShareVisualContent } from 'src/app/pages/starterDashboard/shared/components';

import { useShareVisualModalBody } from '../../../../useShareVisualModalBody';

type Petition = StarterDashboardPageData['petition'];
type Context = 'starter_dashboard_grow_petition';
type Props = { petition: Petition; context: Context; closeModal: () => void };

export const ShareVisualModalBody: React.FC<Props> = ({ petition, context, closeModal }) => {
	const { data, actions } = useShareVisualModalBody({ petition, context });

	if (data.shareVisualsState === 'loading')
		return (
			<Flex
				sx={{
					flexDirection: 'column',
					marginX: '8px',
					height: ['466px', '436px'],
					justifyContent: 'center',
				}}
			>
				<Loader role="progressbar" size="s" mb="20px" mx="auto" />
				<Text as="div" size="large" sx={{ textAlign: 'center' }}>
					<Translate value="corgi.petition.starter_dashboard.shared.share_visuals.modal_loading_copy" />
				</Text>
			</Flex>
		);

	if (data.shareVisualsState === 'timeout')
		return (
			<Flex
				sx={{
					flexDirection: 'column',
					marginX: '8px',
					height: ['466px', '436px'],
					justifyContent: 'center',
				}}
			>
				<Text as="div" size="large" sx={{ textAlign: 'center' }}>
					<Translate value="corgi.petition.starter_dashboard.shared.share_visuals.modal_timeout_copy" />
				</Text>
			</Flex>
		);

	return (
		<form
			onSubmit={actions.onShareVisualDownload}
			sx={{ display: 'flex', flexDirection: 'column', gap: 24, marginTop: 24 }}
			data-qa="share-visual-modal-form"
		>
			<ShareVisualContent petition={petition} context={context} onChange={actions.onShareVisualSelect} />
			<Flex sx={{ flexDirection: ['column', 'row', 'row'], gap: 16 }}>
				<Button variant="secondary" sx={{ flex: 1 }} data-qa="share-visual-modal-cancel" onClick={closeModal}>
					<Text breakWord>
						<Translate value="corgi.petition.starter_dashboard.shared.share_visuals.modal_cancel_button" />
					</Text>
				</Button>
				<Button type="submit" disabled={data.disabled} sx={{ flex: 1 }} data-qa="share-visual-modal-download">
					<Text breakWord>
						<Translate value="corgi.petition.starter_dashboard.shared.share_visuals.modal_action_button" />
					</Text>
				</Button>
			</Flex>
		</form>
	);
};
